import { useEffect, useState } from "react";
import { Crown, Search, Shield } from "lucide-react";
import { api, errorText } from "@/lib/api";
import SectionTitle from "@/components/site/SectionTitle";

const medal = (i) => (i === 0 ? "text-sky-300" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-400" : "text-slate-600");

const Row = ({ i, cells, testid }) => (
  <tr className="border-t border-white/5 hover:bg-indigo-400/[0.06] transition-colors" data-testid={testid}>
    <td className={`py-3 pl-5 pr-2 font-display text-lg w-12 ${medal(i)}`}>{i < 3 ? <Crown size={16} /> : i + 1}</td>
    {cells.map(([col, value], k) => <td key={col} className={`py-3 px-3 ${k === 0 ? "text-slate-100 font-semibold" : "text-slate-400 font-mono text-sm"}`}>{value}</td>)}
  </tr>
);

export default function RankingSection() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("players");
  useEffect(() => { api.get("/ranking").then((r) => setData(r.data)).catch(() => setData({ unavailable: true, players: [], guilds: [] })); }, []);
  const [q, setQ] = useState("");
  const [found, setFound] = useState(null); // null = brak wyszukiwania
  const [searchErr, setSearchErr] = useState("");
  const [busy, setBusy] = useState(false);
  const search = async (e) => {
    e.preventDefault();
    const text = q.trim();
    if (!text) { setFound(null); setSearchErr(""); return; }
    setBusy(true); setSearchErr("");
    try { setFound((await api.get("/ranking/search", { params: { q: text } })).data.players); }
    catch (err) { setFound(null); setSearchErr(errorText(err)); }
    finally { setBusy(false); }
  };
  const clear = () => { setQ(""); setFound(null); setSearchErr(""); };
  const list = data?.[tab] || [];

  return (
    <section id="ranking" className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto" data-testid="site-ranking">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <SectionTitle kicker="Hala sławy" title="Ranking" />
        <div className="flex gap-2 mb-12 md:mb-16">
          {[["players", "Gracze", Crown], ["guilds", "Gildie", Shield]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ranking-tab-${k}`}
              className={`flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.2em] border transition-colors ${tab === k ? "border-amber-400 bg-amber-400/15 text-amber-100" : "border-slate-600/50 text-slate-500 hover:text-slate-300"}`} style={{ clipPath: "polygon(8px 0,calc(100% - 8px) 0,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px),0 8px)" }}>
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>
      </div>
      <form onSubmit={search} className="flex gap-3 mb-6 flex-col sm:flex-row" data-testid="ranking-search-form">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj postaci..." maxLength={24} className="steel-input !mt-0 sm:max-w-sm" data-testid="ranking-search-input" />
        <button disabled={busy} className="btn btn-primary sm:min-w-[140px]" data-testid="ranking-search-btn"><Search size={16} /> Szukaj</button>
        {(found !== null || searchErr) && <button type="button" onClick={clear} className="btn sm:min-w-[140px]" data-testid="ranking-search-clear">Wyczyść</button>}
      </form>
      {searchErr && <p className="text-sm text-red-300 mb-4" data-testid="ranking-search-error">{searchErr}</p>}
      {found !== null && (
        <div className="steel-panel chamfer mb-8"><div className="steel-inner chamfer overflow-x-auto">
          <table className="w-full" data-testid="ranking-search-table">
            <thead className="text-[11px] tracking-[0.25em] text-slate-500 text-left"><tr><th className="py-4 pl-5">Miejsce</th><th className="py-4 px-3">Postać</th><th className="py-4 px-3">Klasa</th><th className="py-4 px-3">Poziom</th></tr></thead>
            <tbody>
              {found.map((p) => (
                <tr key={p.name} className="border-t border-white/5" data-testid={`ranking-found-${p.name}`}>
                  <td className="py-3 pl-5 font-mono text-amber-300">#{p.pos}</td><td className="py-3 px-3 text-slate-100 font-semibold">{p.name}</td>
                  <td className="py-3 px-3 text-slate-400">{p.job}</td><td className="py-3 px-3 text-slate-400 font-mono">{p.level}</td>
                </tr>
              ))}
              {!found.length && <tr><td colSpan={4} className="py-10 text-center text-slate-500" data-testid="ranking-search-empty">Nie znaleziono postaci.</td></tr>}
            </tbody>
          </table>
        </div></div>
      )}
      <div className="steel-panel chamfer"><div className="steel-inner chamfer overflow-x-auto">
        <table className="w-full" data-testid="ranking-table">
          <thead className="text-[11px] tracking-[0.25em] text-slate-500 text-left">
            <tr>
              <th className="py-4 pl-5">#</th>
              {(tab === "players" ? ["Postać", "Klasa", "Poziom"] : ["Gildia", "Poziom", "W / R / P"]).map((h) => <th key={h} className="py-4 px-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {tab === "players" && list.map((p, i) => <Row key={p.name} i={i} cells={[["name", p.name], ["job", p.job], ["level", p.level]]} testid={`ranking-player-${i}`} />)}
            {tab === "guilds" && list.map((g, i) => <Row key={g.name} i={i} cells={[["name", g.name], ["level", g.level], ["wdl", `${g.win} / ${g.draw} / ${g.loss}`]]} testid={`ranking-guild-${i}`} />)}
            {data && !list.length && (
              <tr><td colSpan={4} className="py-12 text-center text-slate-500" data-testid="ranking-empty">
                {data.unavailable ? "Ranking będzie dostępny po starcie serwera." : "Brak danych."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div></div>
    </section>
  );
}
